import { useState } from 'react';
import { audioRecords, chatSessions } from '@/mocks/collections';
import SectionHead from './Section';

const waveA = [3, 6, 4, 8, 5, 9, 7, 4, 8, 6, 9, 5, 7, 8, 4, 6, 9, 7, 5, 8];

export default function MediaSection() {
  const [playing, setPlaying] = useState<string | null>('a1');

  return (
    <section id="media" className="bg-paper-100 py-10 md:py-14 scroll-mt-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <SectionHead
          kicker="录音曝光 · 聊天记录"
          title="声音与对话，不骗人"
          desc="录音曝光配逐句转写，聊天记录按会话归档。原声与字面往往比文案更有说服力，眼见为实，耳听也为实。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* 录音曝光 */}
          <div id="audio" className="scroll-mt-16">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-xl font-black text-ink-950">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-gold-500 text-ink-950">
                  <i className="ri-disc-line" />
                </span>
                录音曝光
                <span className="rounded-full bg-paper-200 px-2 py-0.5 text-xs text-ink-600">声音档案</span>
              </h3>
              <a href="#media" className="cursor-pointer text-sm font-bold text-gold-700 hover:underline">
                全部录音
              </a>
            </div>

            <ul className="space-y-3">
              {audioRecords.map((r) => {
                const isPlay = playing === r.id;
                return (
                  <li
                    key={r.id}
                    className={`group overflow-hidden rounded-xl border p-3.5 transition-colors ${
                      isPlay ? 'border-gold-500 bg-gold-50' : 'border-paper-200 bg-paper-50 hover:border-gold-400'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        aria-label="播放"
                        onClick={() => setPlaying(isPlay ? null : r.id)}
                        className={`grid h-11 w-11 shrink-0 cursor-pointer place-items-center rounded-full transition-colors ${
                          isPlay ? 'bg-gold-600 text-white' : 'bg-ink-950 text-gold-300 group-hover:bg-flame-600 group-hover:text-white'
                        }`}
                      >
                        <i className={`${isPlay ? 'ri-pause-mini-line' : 'ri-play-mini-line'} text-xl`} />
                      </button>
                      <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full ring-2 ring-paper-200">
                        <img src={r.avatar} alt={r.arch} className="h-full w-full object-cover object-top" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 text-[11px] font-bold text-ink-500">
                          <span className="rounded bg-gold-100 px-1.5 py-0.5 text-gold-800">{r.tag}</span>
                          {r.arch}
                        </p>
                        <p className="mt-0.5 truncate font-bold text-ink-950">{r.title}</p>
                      </div>
                      <div className="shrink-0 text-right text-xs text-ink-500">
                        <p className="font-bold text-ink-800">{r.length}</p>
                        <p className="mt-0.5 flex items-center justify-end gap-1">
                          <i className="ri-play-circle-line text-gold-600" />
                          {r.plays} 播放
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex h-7 items-end gap-[2px] px-1">
                      {waveA.map((h, i) => (
                        <span
                          key={i}
                          className={`flex-1 rounded-sm ${
                            isPlay ? (i % 3 === 0 ? 'bg-gold-500 animate-fade-pulse' : 'bg-gold-400') : 'bg-paper-300 group-hover:bg-gold-200'
                          }`}
                          style={{ height: `${h * 3 + 4}px` }}
                        />
                      ))}
                    </div>
                    <p className="mt-2 flex items-center justify-between text-[11px] text-ink-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {r.timestamp} 存档
                      </span>
                      {isPlay ? (
                        <span className="font-bold text-gold-700">正在播放 00:00 — {r.length}</span>
                      ) : (
                        <span className="group-hover:hidden">点击试听关键切片</span>
                      )}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* 聊天记录 */}
          <div id="chat" className="scroll-mt-16">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-xl font-black text-ink-950">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-flame-500 text-white">
                  <i className="ri-chat-smile-3-line" />
                </span>
                聊天记录
                <span className="rounded-full bg-paper-200 px-2 py-0.5 text-xs text-ink-600">会话档案</span>
              </h3>
              <a href="#chat" className="cursor-pointer text-sm font-bold text-flame-700 hover:underline">
                全部会话
              </a>
            </div>

            <ul className="space-y-3">
              {chatSessions.map((c) => (
                <li
                  key={c.id}
                  className="cursor-pointer overflow-hidden rounded-xl border border-paper-200 bg-paper-50 p-3.5 transition-colors hover:border-flame-300 hover:bg-flame-50/40"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-paper-200 text-flame-600">
                      <i className="ri-chat-3-line text-lg" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-ink-950">{c.name}</p>
                      <p className="text-xs text-ink-500">{c.sub}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="flex items-center gap-1 text-[11px] text-ink-400">
                        可信度 <b className="font-display text-lg text-gold-600">{c.score}</b>
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5 rounded-lg bg-ink-950/95 p-3">
                    {c.snippet.map((line, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${i % 2 ? 'bg-gold-400' : 'bg-flame-500'}`} />
                        <p className="text-xs leading-5 text-paper-100/90">「{line}」</p>
                      </div>
                    ))}
                  </div>

                  <p className="mt-2.5 flex items-center gap-2 text-xs font-semibold text-ink-600">
                    <i className="ri-verified-badge-line text-flame-500" />
                    判定：
                    <span className="text-ink-900">{c.verdict}</span>
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}